import { useMeQuery, usePlatformSettingsQuery, useResetWorkspaceSettingMutation, useSetPlatformSettingMutation, useSetWorkspaceSettingMutation, useWorkspaceSettingsQuery } from '@platform/state/api';
import { useFailToast } from '../../system/useFailToast';
import type { IFieldLayout } from './IFieldLayout';
import type { IFieldLayoutState } from './IFieldLayoutState';
import { layoutDefaults } from './layoutDefaults';
import { layoutOverride, mergeLayout } from './mergeLayout';

const KEY = 'fieldLayout';

/**
 * «Розміщення полів» (прототип v4 settings › layout): глобально — налаштування платформи fieldLayout (зберігаються лише змінені параметри,
 * сервер зливає їх із поточним значенням; змінює адміністратор платформи); у просторі — перевизначення простору лише з параметрами, що
 * відрізняються від глобальних (PUT /workspaces/{id}/settings/fieldLayout; без відмінностей — DELETE, «Використовувати глобальні»).
 */
export function useFieldLayout(ws: string, global: boolean): IFieldLayoutState {
  const { data: me } = useMeQuery();
  const { data: platform } = usePlatformSettingsQuery();
  const { data: local } = useWorkspaceSettingsQuery(ws, { skip: global || !ws });
  const [setPlatform] = useSetPlatformSettingMutation();
  const [setLocal] = useSetWorkspaceSettingMutation();
  const [resetLocal] = useResetWorkspaceSettingMutation();
  const fail = useFailToast();
  const globalLayout = mergeLayout(layoutDefaults, platform?.[KEY]);
  const base = global ? layoutDefaults : globalLayout;
  const layout = global ? globalLayout : mergeLayout(globalLayout, local?.[KEY]);
  const canEdit = global ? me?.globalRole === 'Platform admin' : !!me?.workspaces.find((w) => w.id === ws)?.myCapabilities.includes('customize');
  const save = (next: IFieldLayout) => {
    if (global) {
      const patch = layoutOverride(next, globalLayout);
      if (Object.keys(patch).length) setPlatform({ key: KEY, value: patch }).unwrap().catch(fail);
      return;
    }
    const override = layoutOverride(next, globalLayout);
    const request = Object.keys(override).length ? setLocal({ ws, key: KEY, value: override }) : resetLocal({ ws, key: KEY });
    request.unwrap().catch(fail);
  };
  return {
    layout, base, loaded: !!platform && (global || !!local), canEdit,
    overridden: !global && !!local?.[KEY],
    changed: global && Object.keys(layoutOverride(globalLayout, layoutDefaults)).length > 0,
    save,
    useGlobal: () => { resetLocal({ ws, key: KEY }).unwrap().catch(fail); },
    resetDefaults: () => { setPlatform({ key: KEY, value: layoutDefaults }).unwrap().catch(fail); },
  };
}
